import { useParams } from "react-router";
import SessionCard from "../../components/ui/SessionCard";
import { formatDate } from "../Quiz/utils/utils";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { downloadCSV } from "./utils";
import { useQuizResponses } from "../../hooks/useQuizResponses";
import { Helmet } from "react-helmet-async";
import { Download } from "lucide-react";
import { useState } from "react";
import { fetchAllQuizSessions } from "../../services/quizSessionService";

function QuizResponsePage() {
  const { quiz_id } = useParams()
  const {
    sessions,
    quizData,
    loading,
    error,
    responseCount,
    averageScore,
    loadMore,
    hasMore,
    isFetchingMore,
  } = useQuizResponses(quiz_id)

  const [downloading, setDownloading] = useState(false);

  let pageTitle = "Quiz Responses";
  if (quizData?.title) {
    pageTitle = `${quizData.title} Responses`;
  }

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      const allSessions = await fetchAllQuizSessions(quizData.id);
      downloadCSV(quizData, allSessions);
    } catch (err) {
      // Optionally show a toast or error message
      alert("Failed to download CSV. Please try again.");
    }
    setDownloading(false);
  };

  if (error) {
    return (
      <div className="my-10 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (loading && !quizData) {
    return (
      <div className="my-10 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading quiz data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle} | ExamPrep</title>
      </Helmet>
      <div className="container mx-auto px-4">
        <Breadcrumbs
          items={[
            { text: "All quizzes", url: "/admin/quizzes" },
            {
              text: quizData?.topic_detail.course_detail.title,
            },
          ]}
          className="mb-5"
        />

        <div className="bg-gray-50 rounded-xl border-b">
          <div className="container mx-auto px-4 py-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-4">
                <h1 className="text-3xl font-bold text-gray-800">
                  {quizData.topic_detail.course_detail.title}
                </h1>
                <p className="text-lg text-gray-600">
                  {quizData.topic_detail.course_detail.code} • Level{" "}
                  {quizData.topic_detail.course_detail.level}
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h2 className="text-xl font-semibold text-blue-800 mb-2">
                  {quizData.topic_detail.title}
                </h2>
                <p className="text-blue-700 mb-3">
                  {quizData.topic_detail.description}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-blue-600">
                  <span>Quiz Type: {quizData.quiz_type}</span>
                  <span>Expected Duration: {quizData.expected_duration} minutes</span>
                  <span>Created: {formatDate(quizData.created_at)}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 border">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Total Sessions
                  </h3>
                  <p className="text-2xl font-bold text-gray-800">
                    {responseCount}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Average Score
                  </h3>
                  <p className="text-2xl font-bold text-green-600">
                    {averageScore}%
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Completion Rate
                  </h3>
                  <p className="text-2xl font-bold text-blue-600">100%</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Student Results</h2>
              <button
                onClick={handleDownloadCSV}
                disabled={sessions.length === 0 || downloading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                {downloading ? "Downloading..." : "Download CSV"}
              </button>
            </div>

            {loading && sessions.length === 0 ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading results...</p>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No quiz sessions found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map((session) => (
                  <SessionCard key={session.id} session={session} />
                ))}

                {hasMore && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={loadMore}
                      disabled={isFetchingMore}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg"
                    >
                      {isFetchingMore ? "Loading..." : "Load More"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default QuizResponsePage;
